import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Link inválido. Solicite um novo em 'Esqueci minha senha'.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 8) return setError("A senha precisa ter pelo menos 8 caracteres.");
    if (pw1 !== pw2) return setError("As senhas não coincidem.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50">
      <div className="w-full max-w-sm bg-white border border-slate-200 p-8" data-testid="reset-page">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/vyntric-favicon.png" alt="Vyntric" className="w-9 h-9 object-contain" />
          <div className="leading-tight">
            <div className="text-sm font-black tracking-tight">Vyntric</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Nova senha</div>
          </div>
        </div>

        {done ? (
          <div data-testid="reset-success">
            <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-black tracking-tighter">Senha redefinida</h1>
            <p className="text-sm text-slate-600 mt-2">
              Pronto! Estamos te redirecionando para o login…
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black tracking-tighter">Criar nova senha</h1>
            <p className="text-sm text-slate-600 mt-1 mb-6">
              Escolha uma senha forte com pelo menos 8 caracteres.
            </p>
            <form onSubmit={onSubmit} data-testid="reset-form">
              <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700">
                Nova senha
              </label>
              <input
                data-testid="reset-pw1"
                type="password"
                required
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                minLength={8}
                autoFocus
                className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002EA6] focus:ring-1 focus:ring-[#002EA6]"
                placeholder="••••••••"
              />

              <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mt-4">
                Confirmar nova senha
              </label>
              <input
                data-testid="reset-pw2"
                type="password"
                required
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                minLength={8}
                className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#002EA6] focus:ring-1 focus:ring-[#002EA6]"
                placeholder="••••••••"
              />

              {error && (
                <div data-testid="reset-error" className="mt-4 px-3 py-2 text-xs bg-red-50 text-red-800 border border-red-200">
                  {error}
                </div>
              )}

              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading || !token}
                className="mt-5 w-full bg-[#002EA6] hover:bg-[#00207A] text-white font-bold text-sm tracking-wide py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Redefinir senha
              </button>
            </form>
          </>
        )}

        <div className="mt-6 pt-4 border-t border-slate-100 text-center text-xs text-slate-500">
          <Link to="/login" data-testid="reset-back-login" className="text-[#002EA6] font-bold hover:underline">
            Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}
